// 🔧 draft_queue.js
// ✅ Purpose: Admin-facing Draft Queue for Let's Go Vote Sales Automation
// ✅ Features:
// - Shows researched prospects that need outreach drafts
// - Prioritizes by queue priority, fit score, and research confidence
// - Allows admins to generate outreach drafts directly
// - Allows admins to skip drafting without deleting the prospect
// - Routes completed drafts into the Outreach Queue
// - Does NOT send emails or social messages automatically
// - Uses top-of-page success/error/info notifications
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import SalesPageLayout from "../../components/sales/SalesPageLayout";

function DraftQueue() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [draftingId, setDraftingId] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [runningDraftManager, setRunningDraftManager] = useState(false);

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus(null), 20000);
  };

  const loadProspects = async () => {
    try {
      setLoading(true);

      const prospectsSnap = await getDocs(
        query(collection(db, "prospects"), orderBy("createdAt", "desc"))
      );

      setProspects(
        prospectsSnap.docs.map((prospectDoc) => ({
          id: prospectDoc.id,
          ...prospectDoc.data(),
        }))
      );
    } catch (error) {
      console.error("❌ Failed to load Draft Queue:", error);
      showStatus("error", "Failed to load Draft Queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProspects();
  }, []);

  const draftQueue = useMemo(() => {
    return prospects
      .filter((prospect) => {
        const needsDraftStage = prospect.collectionStage === "needs_draft";
        const researchComplete = prospect.researchStatus === "completed";

        const missingDraft =
          !prospect.outreachDraft &&
          !prospect.emailOutreachDraft &&
          prospect.outreachDraftStatus !== "completed";

        const notContacted =
          prospect.status !== "contacted" &&
          prospect.outreachStatus !== "sent";

        return (needsDraftStage || researchComplete) && missingDraft && notContacted;
      })
      .sort((a, b) => {
        const priorityA = Number(a.queuePriority || a.fitScore || 0);
        const priorityB = Number(b.queuePriority || b.fitScore || 0);

        if (priorityB !== priorityA) return priorityB - priorityA;

        const confidenceA = Number(a.researchConfidence || 0);
        const confidenceB = Number(b.researchConfidence || 0);

        if (confidenceB !== confidenceA) return confidenceB - confidenceA;

        const createdA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const createdB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;

        return createdA - createdB;
      });
  }, [prospects]);

  // ✅ Runs Draft Manager
  // ✅ Purpose: Processes the highest-priority draft queue items automatically
  // ✅ Safety: Creates drafts only; does not send emails or social messages
  const handleRunDraftManager = async () => {
    try {
      setRunningDraftManager(true);

      showStatus(
        "info",
        "Draft Manager is processing the Draft Queue."
      );

      const functions = getFunctions();
      const runDraftManager = httpsCallable(functions, "runDraftManager");

      const result = await runDraftManager({
        draftLimit: 5,
      });

      console.log("✅ Draft Manager result:", result.data);

      await loadProspects();

      showStatus(
        "success",
        `Draft Manager complete. Completed: ${result.data.metrics?.completed || 0}. Failed: ${result.data.metrics?.failed || 0}.`
      );
    } catch (error) {
      console.error("❌ Draft Manager failed:", error);

      showStatus(
        "error",
        "Draft Manager failed. Check console for details."
      );
    } finally {
      setRunningDraftManager(false);
    }
  };

  const handleGenerateDraft = async (prospect) => {
    try {
      setDraftingId(prospect.id);

      showStatus("info", `Generating outreach draft for ${prospect.eventName}.`);

      const functions = getFunctions();
      const generateResearchOutreachDraft = httpsCallable(
        functions,
        "generateResearchOutreachDraft"
      );

      await generateResearchOutreachDraft({
        prospectId: prospect.id,
      });

      await updateDoc(doc(db, "prospects", prospect.id), {
        collectionStage: "ready_for_review",
        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus(
        "success",
        `Draft generated for ${prospect.eventName}. It is now in the Outreach Queue.`
      );
    } catch (error) {
      console.error("❌ Draft Agent failed:", error);
      showStatus("error", "Draft Agent failed.");
    } finally {
      setDraftingId(null);
    }
  };

  const handleSkipDraft = async (prospect) => {
    const confirmed = window.confirm(
      `Skip draft generation for "${prospect.eventName}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setWorkingId(prospect.id);

      await updateDoc(doc(db, "prospects", prospect.id), {
        collectionStage: "draft_skipped",
        outreachDraftStatus: "skipped",
        queueReason: "Draft skipped from Draft Queue.",
        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus("success", `${prospect.eventName} draft skipped.`);
    } catch (error) {
      console.error("❌ Failed to skip draft:", error);
      showStatus("error", "Failed to skip draft.");
    } finally {
      setWorkingId(null);
    }
  };

return (
<SalesPageLayout
    title="Draft Queue"
    subtitle="Researched prospects ready for AI outreach draft generation."
    status={status}
    actions={
    <>

        <button
          type="button"
          className="btn btn-success"
          onClick={handleRunDraftManager}
          disabled={runningDraftManager}
        >
          {runningDraftManager
            ? "Running Draft Manager..."
            : "Run Draft Manager"}
        </button>

        <Link className="btn btn-outline-secondary" to="/admin/sales-dashboard">
        Sales Dashboard
        </Link>

        <Link className="btn btn-outline-secondary" to="/admin/outreach-queue">
        Outreach Queue
        </Link>

        <Link className="btn btn-outline-secondary" to="/admin/prospects">
        Prospects CRM
        </Link>
    </>
    }
>
    {loading ? (
        <div className="alert alert-info">Loading Draft Queue...</div>
      ) : draftQueue.length === 0 ? (
        <div className="alert alert-info">No researched prospects currently need drafts.</div>
      ) : (
        <div className="row">
          {draftQueue.map((prospect) => (
            <div className="col-12 mb-3" key={prospect.id}>
              <div className="card">
                <div className="card-header d-flex justify-content-between">
                  <div>
                    <strong>{prospect.eventName}</strong>
                    {prospect.organizationName && (
                      <div className="small text-muted">
                        {prospect.organizationName}
                      </div>
                    )}
                  </div>

                  <span className="badge bg-success">
                    Score {prospect.fitScore || 0}/10
                  </span>
                </div>

                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <strong>Research:</strong>{" "}
                      {prospect.researchConfidence
                        ? `${prospect.researchConfidence}%`
                        : "Completed"}
                    </div>

                    <div className="col-md-3">
                      <strong>Voting:</strong> {prospect.votingType || "Unknown"}
                    </div>

                    <div className="col-md-3">
                      <strong>Current Method:</strong>{" "}
                      {prospect.currentMethod || "Unknown"}
                    </div>

                    <div className="col-md-3">
                      <strong>Stage:</strong>{" "}
                      {prospect.collectionStage || "needs_draft"}
                    </div>
                  </div>

                  {prospect.researchSummary && (
                    <div className="border rounded bg-light p-2 mb-3 small">
                      <strong>Research Summary:</strong>
                      <div>{prospect.researchSummary}</div>
                    </div>
                  )}

                  {prospect.salesAngle && (
                    <div className="border rounded bg-light p-2 mb-3 small">
                      <strong>Sales Angle:</strong>
                      <div>{prospect.salesAngle}</div>
                    </div>
                  )}

                  {prospect.recommendedNextStep && (
                    <div className="small text-muted mb-3">
                      <strong>Recommended Next Step:</strong>{" "}
                      {prospect.recommendedNextStep}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-success me-2"
                    onClick={() => handleGenerateDraft(prospect)}
                    disabled={draftingId === prospect.id}
                  >
                    {draftingId === prospect.id
                      ? "Generating Draft..."
                      : "Generate Draft"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={() => handleSkipDraft(prospect)}
                    disabled={workingId === prospect.id}
                  >
                    Skip
                  </button>

                  <Link className="btn btn-outline-primary" to="/admin/prospects">
                    Edit in CRM
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
    </SalesPageLayout>
    );
}

export default DraftQueue;